import { useQuery } from '@tanstack/react-query'
import { Armchair, BookOpen, LayoutGrid, Pencil, Trash2, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Breadcrumbs } from '@/components/ui/PageHeader'
import { StatCard } from '@/components/ui/StatCard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { classQuery, useDeleteClass } from '@/features/academic/api'
import { ClassDialog } from '@/features/academic/components/ClassDialog'
import { CurriculumEditor } from '@/features/academic/components/CurriculumEditor'
import { DeleteDialog } from '@/features/academic/components/DeleteDialog'
import { SectionsPanel } from '@/features/academic/components/SectionsPanel'
import type { SchoolClass } from '@/features/academic/types'
import { Can } from '@/features/auth/guards'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatNumber } from '@/utils/format'

export function ClassDetailPage() {
  const { t } = useTranslation('academic')
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const { data: c, isPending, error, refetch } = useQuery(classQuery(id))
  const remove = useDeleteClass()
  const [editOpen, setEditOpen] = useState(false)
  const [deleting, setDeleting] = useState<SchoolClass | null>(null)
  useDocumentTitle(c?.name ?? 'Class')

  if (isPending) return <LoadingState />
  if (error) {
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="class" homeHref="/admin/academic/classes" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )
  }

  const capacity = c.sections.reduce((sum, s) => sum + (s.capacity ?? 0), 0)
  const unlimited = c.sections.some((s) => s.capacity === null)
  const optional = c.subjects.filter((s) => s.isOptional).length
  const ages =
    c.minAge !== null || c.maxAge !== null
      ? t('Ages {{min}}–{{max}}', { min: c.minAge ?? '?', max: c.maxAge ?? '?' })
      : null
  const curriculumKey = c.subjects
    .map((s) => `${s.subjectId}:${s.isOptional ? 1 : 0}:${s.weeklyPeriods ?? ''}:${s.coefficient ?? 1}`)
    .join('|')

  return (
    <>
      <Breadcrumbs
        homeHref="/admin"
        items={[
          { label: t('Academic') },
          { label: t('Classes'), to: '/admin/academic/classes' },
          { label: c.name },
        ]}
      />
      <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold sm:text-[1.625rem]">{c.name}</h1>
            <Badge tone="brand">{t('Level {{level}}', { level: c.level })}</Badge>
            {ages ? <Badge>{ages}</Badge> : null}
          </div>
          <p className="mt-1 text-sm text-muted">
            {t('Sections, class teachers and the subjects taught in this class.')}
          </p>
        </div>
        <Can permission="academic.manage">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" leftIcon={<Pencil />} onClick={() => setEditOpen(true)}>
              {t('Edit class')}
            </Button>
            <Button variant="danger-soft" leftIcon={<Trash2 />} onClick={() => setDeleting(c)}>
              {t('Delete')}
            </Button>
          </div>
        </Can>
      </header>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Students')}
          value={formatNumber(c.studentCount)}
          icon={<UsersRound />}
          tone="brand"
        />
        <StatCard
          label={t('Sections')}
          value={formatNumber(c.sections.length)}
          icon={<LayoutGrid />}
          tone="violet"
        />
        <StatCard
          label={t('Seats')}
          value={capacity > 0 ? formatNumber(capacity) : '—'}
          icon={<Armchair />}
          tone="success"
          hint={
            capacity > 0
              ? t('{{free}} free', { free: formatNumber(Math.max(0, capacity - c.studentCount)) }) +
                (unlimited ? ` · ${t('some sections unlimited')}` : '')
              : t('No capacity set')
          }
        />
        <StatCard
          label={t('Subjects')}
          value={formatNumber(c.subjects.length)}
          icon={<BookOpen />}
          tone="info"
          hint={optional ? t('{{count}} optional', { count: optional }) : t('All compulsory')}
        />
      </div>

      <Card>
        <CardBody>
          <Tabs defaultValue="sections">
            <TabsList aria-label={t('Class details')}>
              <TabsTrigger value="sections" icon={<LayoutGrid />} count={c.sections.length}>
                {t('Sections')}
              </TabsTrigger>
              <TabsTrigger value="curriculum" icon={<BookOpen />} count={c.subjects.length}>
                {t('Curriculum')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="sections">
              <SectionsPanel schoolClass={c} />
            </TabsContent>
            <TabsContent value="curriculum">
              <CurriculumEditor key={curriculumKey} schoolClass={c} />
            </TabsContent>
          </Tabs>
        </CardBody>
      </Card>

      <ClassDialog open={editOpen} onOpenChange={setEditOpen} schoolClass={c} />
      <DeleteDialog
        item={deleting}
        onClose={() => setDeleting(null)}
        title={(x) => t('Delete {{name}}?', { name: x.name })}
        description={(x) =>
          x.studentCount > 0
            ? t(
                '{{count}} students are enrolled in this class. Move them first — a class in use cannot be deleted.',
                { count: x.studentCount, formatted: formatNumber(x.studentCount) },
              )
            : t('Its sections and curriculum will be removed. This cannot be undone.')
        }
        onDelete={async (x) => {
          await remove.mutateAsync(x.id)
          void navigate('/admin/academic/classes')
        }}
        successMessage={(x) => t('{{name}} deleted', { name: x.name })}
      />
    </>
  )
}
